import { useState, useCallback, useRef, useEffect, useMemo, Suspense, type ReactNode, type DragEvent } from 'react'
import { createPortal } from 'react-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import rehypeHighlight from 'rehype-highlight'
import { isComponentJobPath, parseComponentJobPath } from '@secureblob/sdk/componentJobs'

import MermaidBlock from './MermaidBlock'
import CodeMirrorEditor from './CodeMirrorEditor'
import ComponentJobPanel from './ComponentJobPanel'
import { getEditorFontClass, type EditorFontPreference } from './editorTypography'
import { isTextLike } from '../utils/isTextLike'
import { SkeletonEditor, SkeletonLine } from './Skeleton'
import 'katex/dist/katex.min.css'
import 'highlight.js/styles/github.css'
import { useEditor, useWorkspace, useWorkspaceMetadata } from '../hooks/useWorkspace'
import { useIsMobile } from '../hooks/useIsMobile'
import { timeAgo } from '../utils/writerUtils'
import { WELCOME_BLOB_ID } from '../engine/welcomeWorkspace'
import { Eye, Edit3, Copy, Download, AlertCircle, Check, Loader2, X, GitCompare, Type, Clipboard, Shield, RotateCcw } from 'lucide-react'
import { attachImage } from '../utils/attachImage'
import { htmlToMarkdown, hasRichHtmlContent } from '../utils/htmlToMarkdown'
import { resolveWorkspacePath, isExternalUrl } from '../utils/workspacePath'
import { useToast } from './Toast'
import { lazyWithRecovery } from '../utils/lazyImportRecovery'
import { useBlame } from '../hooks/useBlame'
import type { Workspace, FileStat, DiffState } from '../engine/types'
import { AgentPanel } from './AgentPanel'
import { AgentPicker, getAutoSelectAgent, ensureAgentConfig } from './AgentPicker'
import { useEmbeddedAgents, type SelectionContext } from '../contexts/EmbeddedAgentContext'
import { Sparkles, MessageSquare } from 'lucide-react'
import { EditorView as CM6EditorView } from '@codemirror/view'
import './EditorView.css'

// ── Types ──────────────────────────────────────────────────────

type EditorMode = 'edit' | 'preview' | 'agent' | 'job'
interface EditorViewProps {
    path: string | null
    diffState: DiffState | null
    onExitDiff?: () => void
    onOpenFile: (path: string) => void
    onLoadingChange?: (path: string, loading: boolean) => void
    /** Initial view mode from share link URL — consumed on first file open, then cleared by parent. */
    initialViewMode?: 'edit' | 'preview' | null
    /** Imperative mode request from surrounding UI, e.g. file-browser context actions. */
    requestedViewMode?: 'edit' | 'preview' | 'agent' | 'job' | null
    onRequestedViewModeApplied?: () => void
    /** Reports current view mode to parent (e.g. for share link generation). */
    onViewModeChange?: (mode: 'edit' | 'preview' | 'agent' | 'job') => void
    /** Scroll to a specific 1-based line number after the file opens (§40 §8.1). Consumed and cleared. */
    scrollToLine?: number | null
    onScrollToLineApplied?: () => void
}

interface DiffHunk {
    type: 'addition' | 'deletion' | 'context'
    text: string
    lineOld?: number | null
    lineNew?: number | null
}

interface ToolbarPos {
    top: number
    left: number
}

// ── Helpers ────────────────────────────────────────────────────


function isMarkdown(path: string | null): boolean {
    return path?.endsWith('.md') ?? false
}

function isTextFile(path: string | null, mime?: string): boolean {
    return path != null && isTextLike(path, mime ?? '')
}

function isAgentFile(path: string | null): boolean {
    if (path == null) return false
    // Match agent config files and their transcript folders (xxx.agent.json.transcripts/session-id/)
    return /\.agent\.json$/i.test(path) || /\.agent\.json\.transcripts\//.test(path)
}

function isComponentJobFile(path: string | null): boolean {
    if (path == null) return false
    return isComponentJobPath(path)
}

const SQLITE_EXTENSIONS = ['.db', '.sqlite', '.sqlite3', '.s3db', '.sdb']
function isSqliteDb(path: string | null): boolean {
    if (!path) return false
    const lower = path.toLowerCase()
    return SQLITE_EXTENSIONS.some(ext => lower.endsWith(ext))
}

// Lazy-load SqliteExplorer — only pulled when a .db file is opened
const SqliteExplorer = lazyWithRecovery(
    () => import('./Sqlite/SqliteExplorer'),
    { moduleName: 'SqliteExplorer' },
)

function extractText(node: ReactNode): string {
    if (typeof node === 'string') return node
    if (Array.isArray(node)) return node.map(extractText).join('')
    if (node && typeof node === 'object' && 'props' in node) {
        return extractText((node as { props: { children?: ReactNode } }).props.children)
    }
    return ''
}



/** Image MIME type lookup — covers all common browser-renderable formats */
const IMAGE_MIME_MAP: Record<string, string> = {
    png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
    gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml',
    bmp: 'image/bmp', ico: 'image/x-icon', avif: 'image/avif',
    tiff: 'image/tiff', tif: 'image/tiff', jfif: 'image/jpeg',
}

/** Renders images from workspace FS — binary-first, data-URL fallback for legacy */
function WorkspaceImage({ src, alt, workspace, currentFile }: {
    src?: string; alt?: string
    workspace: { fs: { readText: (p: string) => Promise<string | null>; readBinary: (p: string) => Promise<Uint8Array | null> } }
    currentFile: string
}) {
    const [dataUrl, setDataUrl] = useState<string | null>(null)
    const [error, setError] = useState(false)

    useEffect(() => {
        if (!src) return
        // Reset state when src changes (prevents stale error/loading from previous image)
        setError(false)
        setDataUrl(null)

        // External or data URL — use directly
        if (src.startsWith('data:') || isExternalUrl(src)) {
            setDataUrl(src)
            return
        }

        let cancelled = false
        const absPath = resolveWorkspacePath(currentFile, src)
        const ext = absPath.split('.').pop()?.toLowerCase() ?? ''

        const loadImage = async () => {
            // Try binary first — this is the normal case for workspace images
            const bytes = await workspace.fs.readBinary(absPath)
            if (cancelled) return
            if (bytes && bytes.length > 0) {
                const blob = new Blob(
                    [new Uint8Array(bytes)],
                    { type: IMAGE_MIME_MAP[ext] || 'image/png' }
                )
                setDataUrl(URL.createObjectURL(blob))
                return
            }
            // Legacy fallback: images stored as base64 data-URL text
            try {
                const text = await workspace.fs.readText(absPath)
                if (cancelled) return
                if (text?.startsWith('data:')) {
                    setDataUrl(text)
                    return
                }
            } catch { /* readText may throw on binary — already handled above */ }
            if (!cancelled) setError(true)
        }
        loadImage().catch(() => { if (!cancelled) setError(true) })

        return () => {
            cancelled = true
            // Revoke blob URL to prevent memory leaks
            setDataUrl(prev => {
                if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev)
                return null
            })
        }
    }, [src, workspace, currentFile])

    if (error) return <span className="ws-image-error" title={src}>🖼️ {alt || src}</span>
    if (!dataUrl) return <span className="ws-image-loading">⏳ {alt || 'Loading…'}</span>
    return <img src={dataUrl} alt={alt || ''} className="ws-image" />
}

/** Workspace-aware link — intercepts clicks for internal files, opens external normally */
function WorkspaceLink({ href, children, currentFile, onOpenFile }: {
    href?: string; children?: ReactNode
    currentFile: string; onOpenFile: (path: string) => void
}) {
    if (!href) return <span>{children}</span>

    // External link — render normally
    if (isExternalUrl(href)) {
        return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
    }

    // Workspace-relative link
    const absPath = resolveWorkspacePath(currentFile, href)
    return (
        <a
            href={absPath}
            className="ws-link"
            onClick={(e) => {
                e.preventDefault()
                onOpenFile(absPath)
            }}
        >
            {children}
        </a>
    )
}

// ── Sub-components ─────────────────────────────────────────────

interface PreBlockProps {
    children?: ReactNode
}

/**
 * Pre wrapper that adds a Copy button to fenced code blocks.
 */
function PreBlock({ children, ...props }: PreBlockProps) {
    const [copied, setCopied] = useState(false)

    const handleCopyBlock = useCallback(() => {
        const text = extractText(children)
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }).catch(() => { /* clipboard unavailable — silently ignore */ })
    }, [children])

    return (
        <div className="code-block-wrapper">
            <pre {...props}>{children}</pre>
            <button
                className={`code-copy-btn ${copied ? 'copied' : ''}`}
                onClick={handleCopyBlock}
                title="Copy"
            >
                {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
            </button>
        </div>
    )
}

interface CodeBlockProps {
    children?: ReactNode
    className?: string
    node?: unknown
}

/**
 * Custom code component for react-markdown.
 * Detects ```mermaid blocks and renders them as diagrams.
 */
function CodeBlock({ children, className, ...props }: CodeBlockProps) {
    const isMermaid = className === 'language-mermaid' || className === 'hljs language-mermaid'
    if (isMermaid) {
        return <MermaidBlock>{children}</MermaidBlock>
    }
    return <code className={className} {...props}>{children}</code>
}


interface BreadcrumbsProps {
    path: string | null
    onNavigate?: (path: string) => void
}

function Breadcrumbs({ path, onNavigate }: BreadcrumbsProps) {
    if (!path) return null
    const parts = path.split('/').filter(Boolean)
    const dirParts = parts.slice(0, -1)

    if (dirParts.length === 0) return null

    return (
        <div className="breadcrumbs">
            {dirParts.map((part, i) => (
                <span key={i}>
                    {i > 0 && <span className="breadcrumb-sep">›</span>}
                    <button
                        className={i === dirParts.length - 1 ? 'breadcrumb-current' : 'breadcrumb-part'}
                        onClick={() => onNavigate?.('/' + parts.slice(0, i + 1).join('/'))}
                        type="button"
                    >
                        {part}
                    </button>
                </span>
            ))}
        </div>
    )
}

// ── Encoding Pill ──────────────────────────────────────────────

const COMMON_CHARSETS = [
    { value: 'utf-8', label: 'UTF-8' },
    { value: 'windows-1252', label: 'Windows-1252 (Western)' },
    { value: 'iso-8859-1', label: 'ISO-8859-1 (Latin-1)' },
    { value: 'iso-8859-15', label: 'ISO-8859-15 (Latin-9)' },
    { value: 'utf-16le', label: 'UTF-16 LE' },
    { value: 'shift_jis', label: 'Shift_JIS (Japanese)' },
] as const

interface EncodingPillProps {
    charset: string
    onCharsetChange: (charset: string) => void
    showWriteWarning?: boolean
}

function EncodingPill({ charset, onCharsetChange, showWriteWarning }: EncodingPillProps) {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    // Close on click outside
    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])

    // Close on Escape
    useEffect(() => {
        if (!open) return
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [open])

    const displayLabel = COMMON_CHARSETS.find(c => c.value === charset)?.label ?? charset.toUpperCase()
    const shortLabel = charset.toUpperCase().replace('WINDOWS-', 'WIN-')

    return (
        <div className="encoding-pill-wrapper" ref={ref}>
            <button
                className="btn btn-sm encoding-pill"
                onClick={() => setOpen(!open)}
                title={`Encoding: ${displayLabel}. Click to change.`}
                aria-label={`File encoding: ${displayLabel}`}
                type="button"
            >
                <span className="encoding-pill-label">{shortLabel}</span>
            </button>
            {open && (
                <div className="encoding-dropdown">
                    <div className="encoding-dropdown-header">File Encoding</div>
                    {COMMON_CHARSETS.map(c => (
                        <button
                            key={c.value}
                            className={`encoding-dropdown-item ${c.value === charset ? 'active' : ''}`}
                            onClick={() => { onCharsetChange(c.value); setOpen(false) }}
                            type="button"
                        >
                            <span className="encoding-dropdown-value">{c.label}</span>
                            {c.value === charset && <Check size={14} className="encoding-dropdown-check" />}
                        </button>
                    ))}
                    {showWriteWarning && charset !== 'utf-8' && (
                        <div className="encoding-dropdown-warning">
                            ⚠️ Saves will convert to UTF-8
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

interface WriterBadgeProps {
    stat: FileStat | null
    blameEnabled: boolean
    onToggleBlame?: () => void
}

function WriterBadge({ stat, blameEnabled, onToggleBlame }: WriterBadgeProps) {
    const workspace = useWorkspace()
    if (!stat?.lastWriterKid) return null
    const resolved = workspace.resolveWriterName(stat.lastWriterKid)
    const displayName = resolved.name || stat.lastWriterKid.slice(0, 8)
    const emoji = resolved.emoji
    // In preview mode (no onToggleBlame), render display-only badge
    const interactive = !!onToggleBlame
    return (
        <span
            className={`writer-badge ${blameEnabled ? 'blame-on' : ''}`}
            title={interactive ? (blameEnabled ? 'Click to hide blame' : 'Click to show blame') : `Last edited by ${displayName}`}
            onClick={onToggleBlame}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            style={interactive ? undefined : { cursor: 'default' }}
        >
            {emoji && <span className="writer-badge-emoji">{emoji}</span>}
            Last edited by {displayName}{interactive ? (blameEnabled ? ' · Blame on' : ' · Click to show blame') : ''}
        </span>
    )
}


interface ToolbarAction {
    label: string
    value: string
}

interface MobileMarkdownToolbarProps {
    onInsert: (snippet: string) => void
    disabled: boolean
}

function MobileMarkdownToolbar({ onInsert, disabled }: MobileMarkdownToolbarProps) {
    const actions: ToolbarAction[] = [
        { label: 'B', value: '**text**' },
        { label: 'I', value: '*text*' },
        { label: '</>', value: '`code`' },
        { label: '🔗', value: '[title](https://)' },
        { label: '#', value: '\n# ' },
        { label: '-', value: '\n- ' },
        { label: '>', value: '\n> ' },
        { label: '✓', value: '\n- [ ] ' },
    ]

    return (
        <div className="mobile-md-toolbar">
            {actions.map(action => (
                <button
                    key={action.label}
                    type="button"
                    className="mobile-md-btn"
                    onClick={() => onInsert(action.value)}
                    disabled={disabled}
                >
                    {action.label}
                </button>
            ))}
        </div>
    )
}

// DiffBar and LineAlignedEditor have been replaced by CodeMirrorEditor
// with CM6 gutter extensions (blameGutter + diffBarGutter).


// ── Diff Mode Components ───────────────────────────────────────

type DiffErrorType = 'unavailable' | 'network' | 'unknown'

interface DiffResult {
    loading: boolean
    hunks: DiffHunk[]
    error: string | null
    errorType: DiffErrorType | null
}

function useDiff(workspace: Workspace, path: string | null, commitHash: string | undefined): DiffResult {
    const [state, setState] = useState<DiffResult>({ loading: true, hunks: [], error: null, errorType: null })
    useEffect(() => {
        if (!workspace || !path || !commitHash) {
            setState({ loading: false, hunks: [], error: null, errorType: null })
            return
        }
        let cancelled = false
        setState(s => ({ ...s, loading: true, error: null, errorType: null }))
        workspace.history.diffText(path, commitHash, 'HEAD').then(
            (hunks: DiffHunk[]) => { if (!cancelled) setState({ loading: false, hunks, error: null, errorType: null }) },
            (err: Error) => {
                if (cancelled) return
                // §9.3: Classify error type for user-friendly UI
                const msg = err.message?.toLowerCase() ?? ''
                let errorType: DiffErrorType = 'unknown'
                if (msg.includes('not available') || msg.includes('object_missing') || msg.includes('404') || msg.includes('400')) {
                    errorType = 'unavailable'
                } else if (msg.includes('offline') || msg.includes('network') || msg.includes('fetch')) {
                    errorType = 'network'
                }
                setState({ loading: false, hunks: [], error: err.message, errorType })
            }
        )
        return () => { cancelled = true }
    }, [workspace, path, commitHash])
    return state
}


interface DiffBannerProps {
    diffState: DiffState
    onExit: (() => void) | undefined
    onRestore?: () => void
    restoreLoading?: boolean
    canWrite?: boolean
}

function DiffBanner({ diffState, onExit, onRestore, restoreLoading, canWrite }: DiffBannerProps) {
    const { commitMeta } = diffState
    const fileName = diffState.path.split('/').pop()
    return (
        <div className="diff-banner">
            <div className="diff-banner-content">
                <GitCompare size={16} className="diff-banner-icon" />
                <div className="diff-banner-info">
                    <span className="diff-banner-title">Comparing {fileName}</span>
                    <span className="diff-banner-meta">
                        <span className="diff-banner-commit">
                            {commitMeta.writerEmoji} {commitMeta.writerName}
                            <code>{commitMeta.id}</code>
                            {commitMeta.timestamp ? (
                                <span className="diff-banner-time">{timeAgo(commitMeta.timestamp)}</span>
                            ) : null}
                        </span>
                        <span className="diff-banner-arrow">⟷</span>
                        <span className="diff-banner-current">Current version</span>
                    </span>
                </div>
            </div>
            <div className="diff-banner-actions">
                {canWrite && onRestore && (
                    <button
                        className="diff-banner-restore"
                        onClick={onRestore}
                        disabled={restoreLoading}
                        title="Restore file to this version"
                    >
                        {restoreLoading ? <Loader2 size={14} className="spin" /> : <RotateCcw size={14} />}
                        Restore
                    </button>
                )}
                <button className="diff-banner-exit" onClick={onExit} title="Exit diff view">
                    <X size={16} />
                    Exit diff
                </button>
            </div>
        </div>
    )
}

interface DiffViewProps {
    hunks: DiffHunk[]
    loading: boolean
    error: string | null
    errorType: DiffErrorType | null
}

function DiffView({ hunks, loading, error, errorType }: DiffViewProps) {
    if (loading) {
        return (
            <div className="diff-loading">
                <Loader2 size={20} className="spin" />
                Computing diff…
            </div>
        )
    }
    if (error) {
        // §9.3: User-friendly messages by error type
        const message = errorType === 'unavailable'
            ? 'Previous version is not available — the commit data may have been cleaned up.'
            : errorType === 'network'
                ? "You're offline — diff requires fetching the previous version."
                : error
        return <div className="diff-error"><AlertCircle size={16} /> {message}</div>
    }
    if (hunks.length === 0) {
        return (
            <div className="diff-empty">
                <Check size={20} />
                No changes — file is identical to this commit
            </div>
        )
    }
    return (
        <div className="diff-view">
            {hunks.map((hunk, i) => (
                <div key={i} className={`diff-line diff-line-${hunk.type}`}>
                    <span className="diff-line-gutter">
                        <span className="diff-line-old">{hunk.lineOld ?? ''}</span>
                        <span className="diff-line-new">{hunk.lineNew ?? ''}</span>
                    </span>
                    <span className="diff-line-marker">
                        {hunk.type === 'addition' ? '+' : hunk.type === 'deletion' ? '−' : ' '}
                    </span>
                    <span className="diff-line-text">{hunk.text || '\u00A0'}</span>
                </div>
            ))}
        </div>
    )
}

// ── Main Component ─────────────────────────────────────────────

export default function EditorView({
    path,
    diffState,
    onExitDiff,
    onOpenFile,
    onLoadingChange,
    initialViewMode,
    requestedViewMode,
    onRequestedViewModeApplied,
    onViewModeChange,
    scrollToLine,
    onScrollToLineApplied,
}: EditorViewProps) {
    const workspace = useWorkspace()
    // §95: Skip text editor hooks for SQLite databases — they are binary files
    // handled exclusively by SqliteExplorer. Reading them via readText() causes
    // binary-as-text garbage that bleeds into the next file opened (the stale
    // binary content renders in CodeMirror before effects update, triggering
    // onChange → debounced writeText to the new path → corruption).
    const editorPath = isSqliteDb(path) ? null : path
    const { content, stat, updateContent, saveVersion, awaitingSync } = useEditor(editorPath)
    const { canWrite } = useWorkspaceMetadata()
    const toast = useToast()
    const isWelcome = workspace.blobId === WELCOME_BLOB_ID
    // ── Per-file mode memory ──────────────────────────────────────
    // Keeps each open file's chosen mode (edit/preview/agent) so that
    // switching tabs preserves the user's toggle state.
    const modeByPath = useRef<Map<string, EditorMode>>(new Map())
    const cursorByPath = useRef<Map<string, number>>(new Map())
    // ── Per-file charset memory ───────────────────────────────────
    // Session-only: remembers encoding choice per file path.
    const charsetByPath = useRef<Map<string, string>>(new Map())
    const [charset, setCharset] = useState<string>(() => {
        return (path && charsetByPath.current.get(path)) || 'utf-8'
    })
    // Charset override: holds re-decoded content for display when charset != utf-8.
    // This is READ-ONLY — editing clears it and resets charset to utf-8.
    const [charsetOverride, setCharsetOverride] = useState<string | null>(null)
    const effectiveContent = charsetOverride ?? content
    const defaultMode: EditorMode = isAgentFile(path)
        ? 'agent'
        : isComponentJobFile(path)
            ? (parseComponentJobPath(path!)?.kind === 'source' ? 'edit' : 'job')
        : (isWelcome || !canWrite) ? 'preview' : 'edit'
    const [mode, _setMode] = useState<EditorMode>(() => {
        if (path && modeByPath.current.has(path)) return modeByPath.current.get(path)!
        return defaultMode
    })
    /** Save current CM6 cursor position for the active file before losing it */
    const saveCursor = useCallback(() => {
        const view = editorViewRef.current
        if (path && view) {
            cursorByPath.current.set(path, view.state.selection.main.head)
        }
    }, [path])
    const setMode = useCallback((m: EditorMode) => {
        // Save cursor before switching away from edit mode
        if (mode === 'edit' && m !== 'edit') saveCursor()
        _setMode(m)
        if (path) modeByPath.current.set(path, m)
        onViewModeChange?.(m)
    }, [path, mode, saveCursor, onViewModeChange])
    const openComponentJobFileInEditMode = useCallback((targetPath: string) => {
        modeByPath.current.set(targetPath, 'edit')
        onOpenFile(targetPath)
    }, [onOpenFile])
    const fontKey = path ? `sb-font:${path}` : null
    const [forcedFont, setForcedFont] = useState<EditorFontPreference | null>(null)
    const [blameEnabled, setBlameEnabled] = useState(false)
    const [blameWidth, setBlameWidth] = useState(() => {
        const saved = localStorage.getItem('sb:blame-width')
        return saved ? Math.max(80, Math.min(400, Number(saved))) : 180
    })
    const handleBlameWidthChange = useCallback((w: number) => {
        setBlameWidth(w)
    }, [])
    const handleBlameWidthCommit = useCallback((w: number) => {
        localStorage.setItem('sb:blame-width', String(Math.round(w)))
    }, [])
    const blame = useBlame(blameEnabled ? workspace : null, path, content, saveVersion)
    // Even when blame is disabled, we still compute blame for the diff-bar
    const diffBlame = useBlame(workspace, path, content, saveVersion)
    const editorViewRef = useRef<CM6EditorView | null>(null)

    // ── Stable refs for memoized ReactMarkdown components (Bug 6 flicker fix) ──
    const workspaceRefForMd = useRef(workspace)
    const pathRefForMd = useRef(path)
    const onOpenFileRefForMd = useRef(onOpenFile)
    workspaceRefForMd.current = workspace
    pathRefForMd.current = path
    onOpenFileRefForMd.current = onOpenFile

    // Memoize with [] deps — refs guarantee fresh values without invalidating the object.
    // This prevents ReactMarkdown from unmounting/remounting WorkspaceImage on every render.
    const markdownComponents = useMemo(() => ({
        code: CodeBlock,
        pre: PreBlock,
        img: (props: any) => <WorkspaceImage {...props} workspace={workspaceRefForMd.current} currentFile={pathRefForMd.current!} />,
        a: (props: any) => <WorkspaceLink {...props} currentFile={pathRefForMd.current!} onOpenFile={onOpenFileRefForMd.current} />,
    }), [])  // eslint-disable-line react-hooks/exhaustive-deps
    const diffResult = useDiff(workspace, path, diffState?.commitHash)
    const [restoreLoading, setRestoreLoading] = useState(false)

    // §9.2.1: Restore file to the version from diffState's commit
    const handleRestoreFile = useCallback(async () => {
        if (!diffState || !path || restoreLoading) return
        setRestoreLoading(true)
        try {
            const oldText = await workspace.history.readTextAtCommit(path, diffState.commitHash)
            if (oldText !== null) {
                await workspace.fs.writeText(path, oldText)
            } else {
                // Try binary restore
                const oldBytes = await workspace.history.readBytesAtCommit(path, diffState.commitHash)
                if (oldBytes) {
                    await workspace.fs.writeBytes(path, oldBytes)
                } else {
                    toast('Could not read the previous version — data may have been cleaned up.', { type: 'error' })
                    return
                }
            }
            const fileName = path.split('/').pop() ?? path
            toast(`✅ Restored ${fileName} to previous version`, { type: 'success' })
            onExitDiff?.()
        } catch (e) {
            toast('Restore failed: ' + (e instanceof Error ? e.message : 'unknown error'), { type: 'error' })
        } finally {
            setRestoreLoading(false)
        }
    }, [workspace, path, diffState, restoreLoading, toast, onExitDiff])



    // Selection handling
    const [toolbarPos, setToolbarPos] = useState<ToolbarPos | null>(null)
    const [selectionText, setSelectionText] = useState('')

    const toViewportToolbarPos = useCallback((rect: DOMRect, floatingHeight = 48): ToolbarPos => {
        const margin = 16
        const left = Math.max(margin, Math.min(window.innerWidth - margin, rect.left + rect.width / 2))
        // Prefer above the selection; if not enough space, flip below
        const spaceAbove = rect.top - margin
        if (spaceAbove >= floatingHeight) {
            return { top: rect.top - floatingHeight - 4, left }
        }
        // Not enough space above → place below the selection
        const belowTop = rect.bottom + 4
        // Clamp so it doesn't go off the bottom
        const top = Math.min(belowTop, window.innerHeight - margin - floatingHeight)
        return { top, left }
    }, [])

    const handleSelectionChange = useCallback((text: string, rect: DOMRect | null) => {
        if (text && text.length > 2 && rect) {
            setToolbarPos(toViewportToolbarPos(rect))
            setSelectionText(text)
        } else {
            setToolbarPos(null)
        }
    }, [toViewportToolbarPos])

    // Dismiss floating toolbar when text is deselected in preview mode
    useEffect(() => {
        if (mode !== 'preview') return
        const handler = () => {
            const sel = window.getSelection()
            if (!sel || sel.isCollapsed || !sel.toString().trim()) {
                setToolbarPos(null)
                setSelectionText('')
            }
        }
        document.addEventListener('selectionchange', handler)
        return () => document.removeEventListener('selectionchange', handler)
    }, [mode])

    // ── Embedded agent integration ──────────────────────────────
    const embeddedAgents = useEmbeddedAgents()
    const [showAgentPicker, setShowAgentPicker] = useState(false)
    const [showInlinePrompt, setShowInlinePrompt] = useState(false)
    const [inlinePromptText, setInlinePromptText] = useState('')
    const [isCursorMode, setIsCursorMode] = useState(false)
    const [cursorPromptPos, setCursorPromptPos] = useState<{ top: number; left: number } | null>(null)
    const inlineInputRef = useRef<HTMLInputElement>(null)
    const savedCursorPos = useRef<number | null>(null)  // save editor cursor to restore on Escape
    const inlinePromptContainerRef = useRef<HTMLDivElement>(null)

    // ── Custom right-click context menu ─────────────────────────
    const [ctxMenu, setCtxMenu] = useState<{ top: number; left: number } | null>(null)
    const ctxMenuRef = useRef<HTMLDivElement>(null)

    // Build SelectionContext from CM6 editor state.
    // Returns a selection context (with selectedText) OR a cursor-only context
    // (with cursorLine + surroundingText but empty selectedText).
    const getSelectionContext = useCallback((): SelectionContext | undefined => {
        if (!path) return undefined
        const view = editorViewRef.current

        // ── Selection mode ──
        if (selectionText && view) {
            const { from, to } = view.state.selection.main
            const lineStart = view.state.doc.lineAt(from).number
            const lineEnd = view.state.doc.lineAt(to).number

            // Build surrounding context (lines outside the selection for orientation)
            const CONTEXT_LINES = 10
            const doc = view.state.doc
            const contextBefore: string[] = []
            const contextAfter: string[] = []
            for (let i = Math.max(1, lineStart - CONTEXT_LINES); i < lineStart; i++) {
                contextBefore.push(`${i}: ${doc.line(i).text}`)
            }
            for (let i = lineEnd + 1; i <= Math.min(doc.lines, lineEnd + CONTEXT_LINES); i++) {
                contextAfter.push(`${i}: ${doc.line(i).text}`)
            }
            const surroundingText = [...contextBefore, `--- SELECTED TEXT (lines ${lineStart}–${lineEnd}) ---`, ...contextAfter].join('\n')

            return { filePath: path, lineStart, lineEnd, selectedText: selectionText, surroundingText }
        }

        // Preview mode with selection: no CM6 view
        if (selectionText) {
            return { filePath: path, lineStart: 1, lineEnd: 1, selectedText: selectionText }
        }

        // ── Cursor-only mode ──
        if (view) {
            const cursorPos = view.state.selection.main.head
            const cursorLine = view.state.doc.lineAt(cursorPos).number
            const doc = view.state.doc
            const CONTEXT_LINES = 15
            const lines: string[] = []
            for (let i = Math.max(1, cursorLine - CONTEXT_LINES); i <= Math.min(doc.lines, cursorLine + CONTEXT_LINES); i++) {
                const marker = i === cursorLine ? ' ◀ cursor' : ''
                lines.push(`${i}: ${doc.line(i).text}${marker}`)
            }
            return {
                filePath: path,
                lineStart: cursorLine,
                lineEnd: cursorLine,
                selectedText: '',
                cursorLine,
                surroundingText: lines.join('\n'),
            }
        }

        return undefined
    }, [selectionText, path])

    // Open the inline prompt input (auto-focus reliably)
    const handleOpenInlinePrompt = useCallback((cursorMode = false) => {
        // Save cursor position before stealing focus
        const view = editorViewRef.current
        if (view) {
            savedCursorPos.current = view.state.selection.main.head
        }
        setIsCursorMode(cursorMode)
        setShowInlinePrompt(true)
        setInlinePromptText('')
        // Use rAF to guarantee focus after React render.
        // preventScroll: true is critical — without it the browser scrolls
        // the editor to bring the fixed-position input into view.
        requestAnimationFrame(() => {
            inlineInputRef.current?.focus({ preventScroll: true })
        })
    }, [])

    // Submit inline prompt → start agent run
    const handleSubmitInlinePrompt = useCallback(async (prompt: string) => {
        if (!prompt.trim()) return
        // In selection mode, require selection text
        if (!isCursorMode && !selectionText) return

        setShowInlinePrompt(false)
        setInlinePromptText('')
        setCursorPromptPos(null)

        // Return focus immediately — don't wait for async agent work
        editorViewRef.current?.focus()

        const userMessage = prompt.trim()
        const ctx = getSelectionContext()

        // Check for agent(s) — context is added by formatContextMessage in EmbeddedAgentContext
        const auto = getAutoSelectAgent(workspace)
        if (auto) {
            await embeddedAgents.startRun(auto, userMessage, ctx)
            setToolbarPos(null)
        } else {
            const agentFiles = workspace.fs.list().filter((f: FileStat) => f.path.endsWith('.agent.json'))
            if (agentFiles.length === 0) {
                const defaultPath = ensureAgentConfig(workspace)
                if (!defaultPath) return // read-only — can't create agent
                await embeddedAgents.startRun(defaultPath, userMessage, ctx)
                setToolbarPos(null)
            } else {
                setShowAgentPicker(true)
            }
        }
    }, [isCursorMode, selectionText, workspace, embeddedAgents, getSelectionContext])

    // Close inline prompt — restore cursor and refocus editor
    const handleCloseInlinePrompt = useCallback(() => {
        setShowInlinePrompt(false)
        setInlinePromptText('')
        setIsCursorMode(false)
        setCursorPromptPos(null)
        // Restore cursor position and refocus editor
        const view = editorViewRef.current
        if (view && savedCursorPos.current !== null) {
            const pos = Math.min(savedCursorPos.current, view.state.doc.length)
            view.dispatch({ selection: { anchor: pos } })
            view.focus()
            savedCursorPos.current = null
        }
    }, [])

    // Click-outside dismiss for inline prompt
    useEffect(() => {
        if (!showInlinePrompt) return
        const handler = (e: MouseEvent) => {
            const target = e.target as Node
            // Don't close if clicking inside the prompt itself
            if (inlinePromptContainerRef.current?.contains(target)) return
            // Don't close if clicking the selection toolbar (which contains the prompt in selection mode)
            const toolbar = document.querySelector('.selection-toolbar')
            if (toolbar?.contains(target)) return
            handleCloseInlinePrompt()
        }
        // Use timeout to avoid immediately closing from the same click that opened it
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handler)
        }, 100)
        return () => {
            clearTimeout(timer)
            document.removeEventListener('mousedown', handler)
        }
    }, [showInlinePrompt, handleCloseInlinePrompt])

    // ── Context menu handler ────────────────────────────────────
    const handleEditorContextMenu = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        const menuW = 200, menuH = 160, margin = 8
        let top = e.clientY
        let left = e.clientX
        // Clamp to viewport
        if (top + menuH > window.innerHeight - margin) top = window.innerHeight - margin - menuH
        if (left + menuW > window.innerWidth - margin) left = window.innerWidth - margin - menuW
        if (top < margin) top = margin
        if (left < margin) left = margin
        setCtxMenu({ top, left })
    }, [])

    // Dismiss context menu on click outside or Escape
    useEffect(() => {
        if (!ctxMenu) return
        const handleClick = (e: MouseEvent) => {
            if (ctxMenuRef.current && !ctxMenuRef.current.contains(e.target as Node)) {
                setCtxMenu(null)
            }
        }
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setCtxMenu(null)
        }
        document.addEventListener('mousedown', handleClick)
        document.addEventListener('keydown', handleKey)
        return () => {
            document.removeEventListener('mousedown', handleClick)
            document.removeEventListener('keydown', handleKey)
        }
    }, [ctxMenu])

    const handleAgentPicked = useCallback(async (agentPath: string) => {
        setShowAgentPicker(false)
        setShowInlinePrompt(false)
        setInlinePromptText('')
        setIsCursorMode(false)
        setCursorPromptPos(null)

        // Return focus immediately — don't wait for async agent work
        editorViewRef.current?.focus()

        const prompt = inlinePromptText.trim() || (isCursorMode ? 'Hjälp mig med koden' : 'Analysera denna markering')
        const ctx = getSelectionContext()
        await embeddedAgents.startRun(agentPath, prompt, ctx)
        setToolbarPos(null)
    }, [embeddedAgents, getSelectionContext, inlinePromptText, isCursorMode])

    // ── Ctrl+I / Cmd+I keyboard shortcut ───────────────────────
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            // Don't fire if focus is in another input/textarea (but allow from inline prompt itself)
            const active = document.activeElement
            if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA') && !active.classList.contains('inline-prompt-input')) return

            const isMeta = e.metaKey || e.ctrlKey
            if (isMeta && e.key === 'i') {
                if (!canWrite) return // AI disabled in read-only workspaces
                e.preventDefault()
                const view = editorViewRef.current
                const hasSelection = view && !view.state.selection.main.empty

                if (hasSelection) {
                    // Selection mode: capture selection first, then open prompt
                    const { from, to } = view!.state.selection.main
                    const text = view!.state.doc.sliceString(from, to)
                    if (text.trim().length > 0) {
                        setSelectionText(text)
                        // Position toolbar near the selection
                        const coords = view!.coordsAtPos(from)
                        if (coords) {
                            setToolbarPos(toViewportToolbarPos(new DOMRect(coords.left, coords.top, 0, coords.bottom - coords.top)))
                        }
                    }
                    handleOpenInlinePrompt(false)
                } else {
                    // Cursor-only mode: position prompt near the cursor line
                    if (view) {
                        const cursorPos = view.state.selection.main.head
                        const coords = view.coordsAtPos(cursorPos)
                        if (coords) {
                            const editorRect = view.dom.getBoundingClientRect()
                            const PROMPT_HEIGHT = 44 // approximate prompt height
                            const MARGIN = 8
                            const spaceBelow = window.innerHeight - coords.bottom
                            // Flip above cursor when there's not enough room below
                            const top = spaceBelow < PROMPT_HEIGHT + MARGIN
                                ? Math.max(MARGIN, coords.top - PROMPT_HEIGHT - 4)
                                : coords.bottom + 4
                            setCursorPromptPos({
                                top,
                                left: editorRect.left + editorRect.width / 2,
                            })
                        }
                    }
                    handleOpenInlinePrompt(true)
                }
            }
        }
        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [handleOpenInlinePrompt, toViewportToolbarPos])

    // ── Preview mode selection handler ─────────────────────────
    const handlePreviewMouseUp = useCallback(() => {
        const sel = window.getSelection()
        if (!sel || sel.isCollapsed || !sel.toString().trim()) {
            // Don't clear toolbar if user just clicks — only clear on empty selection
            return
        }
        const text = sel.toString()
        if (text.length > 2) {
            const range = sel.getRangeAt(0)
            const rect = range.getBoundingClientRect()
            setToolbarPos(toViewportToolbarPos(rect))
            setSelectionText(text)
        }
    }, [toViewportToolbarPos])

    const isText = isTextFile(path, stat?.mime)
    const isMd = isMarkdown(path)
    const isAgent = isAgentFile(path)
    const isComponentJob = isComponentJobFile(path)

    // When path changes, restore that file's remembered mode (or apply a sensible default)
    const [prevPath, setPrevPath] = useState(path)
    if (path !== prevPath) {
        // Save cursor from the file we're leaving
        if (prevPath) {
            const view = editorViewRef.current
            if (view) cursorByPath.current.set(prevPath, view.state.selection.main.head)
        }
        setPrevPath(path)
        // Restore remembered charset (or reset to utf-8)
        setCharset((path && charsetByPath.current.get(path)) || 'utf-8')
        setCharsetOverride(null) // clear stale override from previous file
        const remembered = path ? modeByPath.current.get(path) : undefined
        if (isAgentFile(path)) {
            _setMode('agent')
        } else if (remembered) {
            _setMode(remembered)
            // NOTE: onViewModeChange is deferred to useEffect below to avoid
            // setState-during-render ("Cannot update AppShell while rendering EditorView")
        } else if (isComponentJobFile(path)) {
            // When the active path IS the source file (e.g. user clicked main.ts in
            // the file browser), open the code editor.  The ComponentJobPanel is only
            // the default for job-root and companion paths; "Open overview" or clicking
            // the .job folder sets requestedViewMode='job' explicitly.
            const parsed = parseComponentJobPath(path!)
            _setMode(parsed?.kind === 'source' ? 'edit' : 'job')
        } else if (initialViewMode && isTextFile(path)) {
            // Share link specified a view mode — use it for the initial open
            _setMode(initialViewMode)
        } else {
            _setMode((isWelcome || !canWrite) ? 'preview' : 'edit')
        }
    }

    // Deferred: notify parent of mode changes after render (avoids setState-during-render)
    useEffect(() => {
        onViewModeChange?.(mode)
    }, [mode]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!requestedViewMode || !path) return

        if (requestedViewMode === 'agent' && isAgentFile(path)) {
            _setMode('agent')
            onRequestedViewModeApplied?.()
            return
        }

        if (requestedViewMode === 'job' && isComponentJobFile(path)) {
            _setMode('job')
            onRequestedViewModeApplied?.()
            return
        }

        if ((requestedViewMode === 'edit' || requestedViewMode === 'preview') && isTextFile(path, stat?.mime)) {
            _setMode(requestedViewMode)
            onRequestedViewModeApplied?.()
        }
    }, [requestedViewMode, path, stat?.mime, onRequestedViewModeApplied])

    // ── Scroll to line (§40 §8.1 — search results should jump to matching line) ──
    // Also depends on `content` because the CM6 editor is not mounted until content
    // is non-null (the loading guard prevents CodeMirrorEditor from rendering).
    // Without this dep, the effect fires once with editorViewRef===null, never retries.

    useEffect(() => {
        if (scrollToLine == null || scrollToLine <= 0 || !path) return
        if (content === null) return // wait for content to load and CM6 to mount
        const view = editorViewRef.current
        if (!view) return
        const targetLine = Math.min(scrollToLine, view.state.doc.lines)
        if (targetLine < 1) return
        const pos = view.state.doc.line(targetLine).from
        view.dispatch({
            selection: { anchor: pos },
            effects: CM6EditorView.scrollIntoView(pos, { y: 'center' }),
        })
        onScrollToLineApplied?.()
    }, [scrollToLine, path, content, editorViewRef, onScrollToLineApplied])

    // Default typography follows file type; user can override per-file.
    const fontClass = getEditorFontClass(path, forcedFont)

    // Reactive mobile detection
    const isMobile = useIsMobile()

    // Line numbers: auto (mono=on, prose=off), blame forces on, mobile always off.
    // User can override with localStorage 'sb-line-numbers': 'auto' | 'on' | 'off'.
    const showLineNumbers = useMemo(() => {
        const pref = typeof localStorage !== 'undefined'
            ? localStorage.getItem('sb-line-numbers') ?? 'auto'
            : 'auto'
        if (pref === 'on') return !isMobile   // respect mobile even when forced on
        if (pref === 'off') return false
        // auto
        if (isMobile) return false
        if (blameEnabled) return true
        return fontClass === 'editor-mono'
    }, [isMobile, blameEnabled, fontClass])

    const handleCopy = useCallback(() => {
        if (effectiveContent) {
            navigator.clipboard.writeText(effectiveContent).then(() => {
                toast('📋 Copied!', { type: 'success', duration: 1000 })
            }).catch(() => {
                toast('Copy failed', { type: 'error', duration: 2000 })
            })
        }
    }, [effectiveContent, toast])

    const handleDownload = useCallback(() => {
        if (!effectiveContent || !path) return
        const blob = new Blob([effectiveContent], { type: `text/plain;charset=${charset}` })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = path.split('/').pop() || 'download'
        a.click()
        URL.revokeObjectURL(url)
    }, [effectiveContent, path, charset])

    // ── Charset change handler ───────────────────────────────────
    // Re-reads the file using readBinary + TextDecoder with the new charset.
    // IMPORTANT: Charset change is READ-ONLY — it re-decodes the raw bytes
    // for display without triggering a write. This prevents the file from
    // being silently converted to UTF-8, which would cause mojibake on
    // subsequent re-opens with the same charset.
    //
    // When the user actually EDITS after a charset change, the content flows
    // through updateContent → writeText → UTF-8. At that point we reset
    // charset to UTF-8 since the file is now stored as UTF-8.

    const handleCharsetChange = useCallback(async (newCharset: string) => {
        if (!path || newCharset === charset) return

        charsetByPath.current.set(path, newCharset)
        setCharset(newCharset)

        if (newCharset === 'utf-8') {
            // Switching back to UTF-8: clear override, show useEditor content
            setCharsetOverride(null)
            return
        }

        // Non-UTF-8: readBinary + TextDecoder for display only
        try {
            const bytes = await workspace.fs.readBinary(path)
            if (!bytes) return
            const decoded = new TextDecoder(newCharset, { fatal: false }).decode(bytes)
            setCharsetOverride(decoded)
        } catch {
            toast('Unsupported encoding: ' + newCharset, { type: 'error' })
            charsetByPath.current.set(path, charset)
            setCharset(charset)
        }
    }, [path, charset, workspace, toast])

    // When the user edits after a charset change, the override is consumed:
    // the edited text goes through updateContent → writeText (UTF-8), and we
    // reset charset to UTF-8 since the file is now stored as UTF-8.
    const handleUpdateContent = useCallback((newContent: string) => {
        if (charsetOverride !== null) {
            setCharsetOverride(null)
            setCharset('utf-8')
            if (path) charsetByPath.current.set(path, 'utf-8')
        }
        updateContent(newContent)
    }, [charsetOverride, updateContent, path])

    // When the file changes externally (sync) while a non-UTF-8 charset is
    // active, the charsetOverride becomes stale. Re-decode from fresh bytes.
    const prevSaveVersion = useRef(saveVersion)
    useEffect(() => {
        if (prevSaveVersion.current === saveVersion) return // skip initial
        prevSaveVersion.current = saveVersion
        if (charsetOverride === null || !path || charset === 'utf-8') return
        // Re-read with current charset
        workspace.fs.readBinary(path).then(bytes => {
            if (!bytes) return
            setCharsetOverride(new TextDecoder(charset, { fatal: false }).decode(bytes))
        }).catch(() => { /* ignore — file may have been deleted */ })
    }, [saveVersion, charsetOverride, path, charset, workspace])


    // Check if path is an image
    useEffect(() => {
        if (!path) return
        setForcedFont(localStorage.getItem(`sb-font:${path}`) as EditorFontPreference | null)
    }, [path])

    const isImage = path != null && /\.(png|jpe?g|gif|webp|svg|bmp|ico)$/i.test(path)

    // Load image — handles both data-URL text (pasted images) and binary blobs
    const [imageUrl, setImageUrl] = useState<string | null>(null)
    useEffect(() => {
        if (!path || !isImage) { setImageUrl(null); return }
        let revoked = false
        // Try readText first — pasted images are stored as data-URL strings
        workspace.fs.readText(path).then((text: string | null) => {
            if (revoked) return
            if (text && text.startsWith('data:')) {
                setImageUrl(text)
                return
            }
            // Fallback: read as binary blob
            workspace.fs.readBinary(path).then((bytes: Uint8Array | null) => {
                if (revoked) return
                if (!bytes) { setImageUrl(''); return }
                const ext = path.split('.').pop()?.toLowerCase() ?? ''
                const mimeMap: Record<string, string> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml', bmp: 'image/bmp', ico: 'image/x-icon' }
                const blob = new Blob([bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as BlobPart], { type: mimeMap[ext] || 'application/octet-stream' })
                setImageUrl(URL.createObjectURL(blob))
            }).catch(() => { if (!revoked) setImageUrl('') })
        }).catch(() => { if (!revoked) setImageUrl('') })
        return () => {
            revoked = true
            setImageUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null })
        }
    }, [workspace, path, isImage])

    // Report loading state to parent for tab progress bar
    // Images load via imageUrl (not content), so check that separately
    useEffect(() => {
        if (onLoadingChange && path) {
            // SQLite files bypass useEditor (content is always null) — their own
            // loading is handled internally by SqliteExplorer, not by the tab bar.
            // Component job files rendered in job mode also bypass useEditor content;
            // ComponentJobPanel loads its own data and manages its own loading state.
            const isLoading = isSqliteDb(path)
                ? false
                : isComponentJobFile(path) && mode === 'job'
                    ? false
                    : isImage
                        ? imageUrl === null
                        : content === null
            onLoadingChange(path, isLoading)
        }
    }, [path, content === null, imageUrl === null, isImage, onLoadingChange, mode])

    // Must be above all conditional returns to satisfy Rules of Hooks
    const previewContent = effectiveContent ?? ''

    const handleEditorDrop = useCallback((event: DragEvent<HTMLDivElement>) => {
        if (!isMd || !canWrite || !path) return
        const file = event.dataTransfer?.files?.[0]
        if (!file || !file.type.startsWith('image/')) return
        event.preventDefault()
        attachImage({
            blob: file,
            filePath: path,
            writeBytes: (p, b) => workspace.fs.writeBytes(p, b),
            editorView: editorViewRef.current,
            updateContent: handleUpdateContent,
            content: effectiveContent,
            prefix: 'drop',
        }).then(() => toast('📎 Image attached', { type: 'success', duration: 2000 }))
            .catch(() => toast('Failed to attach image', { type: 'error' }))
    }, [canWrite, effectiveContent, isMd, path, handleUpdateContent, workspace, toast])

    // ── Paste handler (images + rich text → markdown) ────────
    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        if (mode !== 'edit' || !isMd || !canWrite || !path) return
        const items = e.clipboardData?.items
        if (!items) return

        // Phase 1: Check for images (existing behavior, untouched)
        for (const item of Array.from(items)) {
            if (!item.type.startsWith('image/')) continue
            e.preventDefault()
            const blob = item.getAsFile()
            if (!blob) continue

            attachImage({
                blob,
                filePath: path,
                writeBytes: (p, b) => workspace.fs.writeBytes(p, b),
                editorView: editorViewRef.current,
                updateContent: handleUpdateContent,
                content: effectiveContent,
                prefix: 'paste',
            }).then(() => toast('📎 Image pasted', { type: 'success', duration: 2000 }))
                .catch(() => toast('Failed to paste image', { type: 'error' }))
            return
        }

        // Phase 2: Check for rich HTML (Excel tables, Word formatting)
        const htmlItem = Array.from(items).find(item => item.type === 'text/html')
        if (htmlItem) {
            const html = e.clipboardData.getData('text/html')
            if (html && hasRichHtmlContent(html)) {
                const md = htmlToMarkdown(html)
                if (md.trim()) {
                    e.preventDefault()
                    const view = editorViewRef.current
                    if (view) {
                        const { from, to } = view.state.selection.main
                        view.dispatch({
                            changes: { from, to, insert: md },
                            selection: { anchor: from + md.length },
                        })
                        view.focus()
                    } else {
                        handleUpdateContent(`${effectiveContent || ''}${md}`)
                    }
                    return
                }
            }
        }
        // Otherwise: let default paste behavior handle text/plain
    }, [mode, isMd, canWrite, path, workspace, effectiveContent, handleUpdateContent, toast])

    const handleInsertMarkdown = useCallback((snippet: string) => {
        if (!canWrite) return
        const view = editorViewRef.current
        if (!view) {
            handleUpdateContent(`${effectiveContent || ''}${snippet}`)
            return
        }
        const { from, to } = view.state.selection.main
        view.dispatch({
            changes: { from, to, insert: snippet },
            selection: { anchor: from + snippet.length },
        })
        view.focus()
    }, [canWrite, effectiveContent, handleUpdateContent])

    if (!path) {
        const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent)
        const mod = isMac ? '⌘' : 'Ctrl'
        return (
            <div className="editor-empty">
                <div className="editor-empty-content">
                    <div className="editor-empty-icon">
                        <Shield size={32} />
                    </div>
                    <h2>Welcome to SecureBlob</h2>
                    <p>Your encrypted workspace. Select a file or create a new one.</p>
                    <div className="editor-empty-actions">
                        <div className="editor-empty-action-row">
                            <span className="editor-empty-action-label">Ask AI</span>
                            <kbd className="editor-empty-kbd">{mod}+I</kbd>
                        </div>
                        <div className="editor-empty-action-row">
                            <span className="editor-empty-action-label">Paste</span>
                            <kbd className="editor-empty-kbd">{mod}+V</kbd>
                        </div>
                        <div className="editor-empty-action-row">
                            <span className="editor-empty-action-label">Quick-open</span>
                            <kbd className="editor-empty-kbd">{mod}+P</kbd>
                        </div>
                    </div>
                </div>

                {/* Cursor-mode prompt also available from empty state */}
                {showInlinePrompt && isCursorMode && (
                    <div className="inline-prompt-cursor-mode">
                        <div className="inline-prompt">
                            <Sparkles size={14} className="inline-prompt-icon" />
                            <input
                                ref={inlineInputRef}
                                className="inline-prompt-input"
                                type="text"
                                placeholder="Fråga AI vad som helst… (Ctrl+I)"
                                value={inlinePromptText}
                                onChange={e => setInlinePromptText(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && inlinePromptText.trim()) {
                                        e.preventDefault()
                                        handleSubmitInlinePrompt(inlinePromptText)
                                    } else if (e.key === 'Escape') {
                                        handleCloseInlinePrompt()
                                    }
                                }}
                            />
                            <button
                                className="inline-prompt-send"
                                disabled={!inlinePromptText.trim()}
                                onClick={() => handleSubmitInlinePrompt(inlinePromptText)}
                                title="Skicka"
                            >
                                <MessageSquare size={14} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    // §95: SQLite database files → SqliteExplorer (must be checked BEFORE
    // content === null guard, since useEditor receives null path for .db files)
    if (isSqliteDb(path)) {
        return (
            <div className="editor-view">
                <Breadcrumbs path={path} onNavigate={onOpenFile} />
                <Suspense fallback={<SkeletonEditor />}>
                    <SqliteExplorer path={path} />
                </Suspense>
            </div>
        )
    }

    if (isComponentJob && mode === 'job' && path) {
        return (
            <div className="editor-view">
                <ComponentJobPanel
                    path={path}
                    onOpenFile={onOpenFile}
                    onOpenFileInEditMode={openComponentJobFileInEditMode}
                    onEditFile={() => setMode('edit')}
                    canWrite={canWrite}
                />
            </div>
        )
    }

    if (content === null && !isImage) {
        // If we have stat but no content yet, it's still loading.
        // If we don't even have stat, the file likely doesn't exist —
        // BUT if awaitingSync is true, initial sync hasn't delivered yet (show loading).
        // Images skip this — they load via readBinary in their own useEffect.
        if (!stat) {
            if (awaitingSync) {
                return (
                    <div className="editor-empty">
                        <div className="editor-empty-content">
                            <Breadcrumbs path={path} onNavigate={onOpenFile} />
                            <div className="editor-binary-info">
                                <div className="sync-spinner" />
                                <h2>Syncing file…</h2>
                                <p className="text-secondary">Fetching <strong>{path.split('/').pop()}</strong> from the workspace</p>
                            </div>
                        </div>
                    </div>
                )
            }
            return (
                <div className="editor-empty">
                    <div className="editor-empty-content">
                        <Breadcrumbs path={path} onNavigate={onOpenFile} />
                        <div className="editor-binary-info">
                            <AlertCircle size={24} className="text-error" />
                            <h2>File not found</h2>
                            <p className="text-secondary">The file <strong>{path}</strong> does not exist in this workspace.</p>
                        </div>
                    </div>
                </div>
            )
        }
        return (
            <div className="editor-loading">
                <SkeletonEditor />
            </div>
        )
    }

    if (!isText && !isImage) {
        return (
            <div className="editor-binary">
                <Breadcrumbs path={path} onNavigate={onOpenFile} />
                <div className="editor-binary-info">
                    <p>Binary file — preview not supported</p>
                    <p className="text-secondary">{stat?.mime} · {stat?.size} bytes</p>
                    <button className="btn" onClick={handleDownload}><Download size={16} /> Download</button>
                </div>
            </div>
        )
    }

    if (isImage) {
        return (
            <div className="editor-binary">
                <Breadcrumbs path={path} onNavigate={onOpenFile} />
                <div className="editor-image-preview">
                    {imageUrl ? (
                        <img src={imageUrl} alt={path.split('/').pop()} className="editor-image content-appear" />
                    ) : (
                        <div className="editor-image-skeleton">
                            <SkeletonLine width="100%" height="200px" style={{ borderRadius: 8 }} />
                        </div>
                    )}
                    <div className="editor-image-meta">
                        <p className="text-secondary">{(() => { const ext = path.split('.').pop()?.toLowerCase() ?? ''; const m: Record<string, string> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml', bmp: 'image/bmp', ico: 'image/x-icon' }; return m[ext] || stat?.mime || 'image'; })()} · {stat?.size} bytes</p>
                        <button className="btn" onClick={handleDownload}><Download size={16} /> Download</button>
                    </div>
                </div>
            </div>
        )
    }

    // If in diff mode, render diff view instead of normal editor
    if (diffState) {
        return (
            <div className="editor-view editor-view-diff">
                <Breadcrumbs path={path} onNavigate={onOpenFile} />
                <DiffBanner diffState={diffState} onExit={onExitDiff} onRestore={handleRestoreFile} restoreLoading={restoreLoading} canWrite={canWrite} />
                <DiffView {...diffResult} />
            </div>
        )
    }

    // Agent file in agent mode — render AgentPanel
    if (isAgent && mode === 'agent') {
        return (
            <div className="editor-view">
                <AgentPanel
                    agentPath={path}
                    workspace={workspace}
                    onOpenFile={onOpenFile}
                    onEditConfig={() => setMode('edit')}
                />
            </div>
        )
    }

    return (
        <div className={`editor-view content-appear${showInlinePrompt ? ' ai-prompt-active' : ''}`} onDrop={handleEditorDrop} onDragOver={(e) => { if (isMd && canWrite) e.preventDefault() }} onPaste={handlePaste} onContextMenu={handleEditorContextMenu}>
            <Breadcrumbs path={path} onNavigate={onOpenFile} />





            {toolbarPos && (
                <div
                    className={`selection-toolbar ${showInlinePrompt && !isCursorMode ? 'selection-toolbar-expanded' : ''}`}
                    style={{ top: toolbarPos.top, left: toolbarPos.left, transform: 'translateX(-50%)' }}
                >
                    {!showInlinePrompt ? (
                        <>
                            {canWrite && (
                                <>
                                    <button className="selection-toolbar-btn" onClick={() => handleOpenInlinePrompt(false)}>
                                        <Sparkles size={14} className="selection-toolbar-agent-icon" />
                                        AI
                                    </button>
                                    <div className="selection-toolbar-sep" />
                                </>
                            )}
                            <button className="selection-toolbar-btn" onClick={() => {
                                navigator.clipboard.writeText(selectionText)
                                setToolbarPos(null)
                            }}>
                                <Copy size={14} />
                                Kopiera
                            </button>
                        </>
                    ) : !isCursorMode ? (
                        <div className="inline-prompt">
                            <Sparkles size={14} className="inline-prompt-icon" />
                            <input
                                ref={inlineInputRef}
                                className="inline-prompt-input"
                                type="text"
                                placeholder="Beskriv vad du vill göra med markeringen…"
                                value={inlinePromptText}
                                onChange={e => setInlinePromptText(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && inlinePromptText.trim()) {
                                        e.preventDefault()
                                        handleSubmitInlinePrompt(inlinePromptText)
                                    } else if (e.key === 'Escape') {
                                        handleCloseInlinePrompt()
                                    }
                                }}
                            />
                            <button
                                className="inline-prompt-send"
                                disabled={!inlinePromptText.trim()}
                                onClick={() => handleSubmitInlinePrompt(inlinePromptText)}
                                title="Skicka"
                            >
                                <MessageSquare size={14} />
                            </button>
                        </div>
                    ) : null}
                </div>
            )}

            {/* Cursor-mode inline prompt — portaled to body to escape content-appear transform */}
            {showInlinePrompt && isCursorMode && createPortal(
                <div
                    ref={inlinePromptContainerRef}
                    className="inline-prompt-cursor-mode"
                    style={cursorPromptPos ? {
                        position: 'fixed',
                        top: cursorPromptPos.top,
                        left: cursorPromptPos.left,
                        transform: 'translateX(-50%)',
                        bottom: 'auto',
                    } : undefined}
                >
                    <div className="inline-prompt">
                        <Sparkles size={14} className="inline-prompt-icon" />
                        <input
                            ref={inlineInputRef}
                            className="inline-prompt-input"
                            type="text"
                            placeholder={path ? "Beskriv vad du vill ändra vid cursorn… (Ctrl+I)" : "Fråga AI vad som helst… (Ctrl+I)"}
                            value={inlinePromptText}
                            onChange={e => setInlinePromptText(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && inlinePromptText.trim()) {
                                    e.preventDefault()
                                    handleSubmitInlinePrompt(inlinePromptText)
                                } else if (e.key === 'Escape') {
                                    handleCloseInlinePrompt()
                                }
                            }}
                        />
                        <button
                            className="inline-prompt-send"
                            disabled={!inlinePromptText.trim()}
                            onClick={() => handleSubmitInlinePrompt(inlinePromptText)}
                            title="Skicka"
                        >
                            <MessageSquare size={14} />
                        </button>
                    </div>
                </div>,
                document.body
            )}

            {showAgentPicker && toolbarPos && (
                <AgentPicker
                    workspace={workspace}
                    anchorRect={toolbarPos}
                    onSelect={handleAgentPicked}
                    onClose={() => setShowAgentPicker(false)}
                />
            )}

            {/* Custom right-click context menu — portaled to body */}
            {ctxMenu && createPortal(
                <div
                    ref={ctxMenuRef}
                    className="editor-ctx-menu"
                    style={{ top: ctxMenu.top, left: ctxMenu.left }}
                >
                    {canWrite && (
                        <button className="editor-ctx-item" onClick={() => {
                            setCtxMenu(null)
                            const view = editorViewRef.current
                            const hasSelection = view && !view.state.selection.main.empty
                            if (hasSelection) {
                                const { from, to } = view!.state.selection.main
                                const text = view!.state.doc.sliceString(from, to)
                                if (text.trim().length > 0) {
                                    setSelectionText(text)
                                    const coords = view!.coordsAtPos(from)
                                    if (coords) {
                                        setToolbarPos(toViewportToolbarPos(new DOMRect(coords.left, coords.top, 0, coords.bottom - coords.top)))
                                    }
                                }
                                handleOpenInlinePrompt(false)
                            } else {
                                if (view) {
                                    const cursorPos = view.state.selection.main.head
                                    const coords = view.coordsAtPos(cursorPos)
                                    if (coords) {
                                        const editorRect = view.dom.getBoundingClientRect()
                                        const promptH = 48, margin = 16
                                        let top = coords.bottom + 4
                                        if (top + promptH > window.innerHeight - margin) {
                                            top = coords.top - promptH - 4
                                        }
                                        top = Math.max(margin, Math.min(top, window.innerHeight - margin - promptH))
                                        setCursorPromptPos({ top, left: editorRect.left + editorRect.width / 2 })
                                    }
                                }
                                handleOpenInlinePrompt(true)
                            }
                        }}>
                            <Sparkles size={14} />
                            <span>AI</span>
                            <kbd>Ctrl+I</kbd>
                        </button>
                    )}

                    {selectionText && (
                        <button className="editor-ctx-item" onClick={() => {
                            navigator.clipboard.writeText(selectionText)
                            setCtxMenu(null)
                            setToolbarPos(null)
                        }}>
                            <Copy size={14} />
                            <span>Kopiera</span>
                            <kbd>Ctrl+C</kbd>
                        </button>
                    )}

                    {canWrite && mode === 'edit' && (
                        <button className="editor-ctx-item" onClick={async () => {
                            setCtxMenu(null)
                            try {
                                const items = await navigator.clipboard.read()
                                const view = editorViewRef.current
                                if (!view) return
                                // Try text/html for rich content first
                                for (const item of items) {
                                    if (item.types.includes('text/html')) {
                                        try {
                                            const blob = await item.getType('text/html')
                                            const html = await blob.text()
                                            if (hasRichHtmlContent(html)) {
                                                const md = htmlToMarkdown(html)
                                                if (md.trim()) {
                                                    const { from, to } = view.state.selection.main
                                                    view.dispatch({ changes: { from, to, insert: md }, selection: { anchor: from + md.length } })
                                                    view.focus()
                                                    return
                                                }
                                            }
                                        } catch { /* fall through */ }
                                    }
                                }
                                // Fallback: plain text
                                const text = await navigator.clipboard.readText()
                                if (text) {
                                    const { from, to } = view.state.selection.main
                                    view.dispatch({ changes: { from, to, insert: text } })
                                }
                            } catch { /* clipboard permission denied */ }
                        }}>
                            <Clipboard size={14} />
                            <span>Klistra in</span>
                            <kbd>Ctrl+V</kbd>
                        </button>
                    )}

                    {canWrite && mode === 'edit' && (
                        <button className="editor-ctx-item" onClick={async () => {
                            setCtxMenu(null)
                            try {
                                const text = await navigator.clipboard.readText()
                                const view = editorViewRef.current
                                if (view && text) {
                                    const { from, to } = view.state.selection.main
                                    view.dispatch({ changes: { from, to, insert: text } })
                                }
                            } catch { /* clipboard permission denied */ }
                        }}>
                            <Type size={14} />
                            <span>Klistra in oformatterat</span>
                        </button>
                    )}

                    {mode === 'edit' && (
                        <button className="editor-ctx-item" onClick={() => {
                            setCtxMenu(null)
                            const view = editorViewRef.current
                            if (view) {
                                view.dispatch({ selection: { anchor: 0, head: view.state.doc.length } })
                                view.focus()
                            }
                        }}>
                            <Type size={14} />
                            <span>Markera allt</span>
                            <kbd>Ctrl+A</kbd>
                        </button>
                    )}
                </div>,
                document.body
            )}


            <div className="editor-toolbar">
                <div className="editor-toolbar-left">
                    <WriterBadge stat={stat} blameEnabled={mode === 'edit' ? blameEnabled : false} onToggleBlame={mode === 'edit' ? () => setBlameEnabled(!blameEnabled) : undefined} />
                </div>
                <div className="editor-toolbar-right">
                    <button className="btn btn-icon btn-sm" onClick={handleCopy} title="Copy"><Copy size={16} /></button>
                    <button className="btn btn-icon btn-sm" onClick={handleDownload} title="Download"><Download size={16} /></button>
                    {mode !== 'preview' && (
                        <button
                            className="btn btn-sm editor-font-btn"
                            onClick={() => {
                                const next: EditorFontPreference = fontClass === 'editor-mono' ? 'prose' : 'mono'
                                setForcedFont(next)
                                if (fontKey) localStorage.setItem(fontKey, next)
                            }}
                            title="Toggle font"
                            aria-label={`Toggle font, currently ${fontClass === 'editor-mono' ? 'mono' : 'prose'}`}
                        >
                            <Type size={14} />
                            <span className="editor-font-prefix">Font:</span>
                            <span className="editor-font-value">{fontClass === 'editor-mono' ? 'Mono' : 'Prose'}</span>
                        </button>
                    )}
                    <EncodingPill charset={charset} onCharsetChange={handleCharsetChange} showWriteWarning={mode === 'edit' && canWrite} />
                </div>
                {isAgent ? (
                    <div className="editor-toolbar-mode">
                        <div className="editor-mode-toggle">
                            <div className="editor-mode-toggle-pill" style={{ transform: mode === 'edit' ? 'translateX(100%)' : 'translateX(0)' }} />
                            <button
                                className={`toggle-btn ${mode === 'agent' ? 'active' : ''}`}
                                onClick={() => setMode('agent')}
                                title="Agent"
                                aria-label="Agent"
                            >
                                <MessageSquare size={14} />
                                <span className="toggle-label">Agent</span>
                            </button>
                            <button
                                className={`toggle-btn ${mode === 'edit' ? 'active' : ''}`}
                                onClick={() => setMode('edit')}
                                title="Edit"
                                aria-label="Edit"
                            >
                                <Edit3 size={14} />
                                <span className="toggle-label">Edit</span>
                            </button>
                        </div>
                    </div>
                ) : isMd ? (
                    <div className="editor-toolbar-mode">
                        <div className="editor-mode-toggle">
                            <div className="editor-mode-toggle-pill" style={{ transform: mode === 'preview' ? 'translateX(100%)' : 'translateX(0)' }} />
                            <button
                                className={`toggle-btn ${mode === 'edit' ? 'active' : ''}`}
                                onClick={() => setMode('edit')}
                                title="Edit"
                                aria-label="Edit"
                            >
                                <Edit3 size={14} />
                                <span className="toggle-label">Edit</span>
                            </button>
                            <button
                                className={`toggle-btn ${mode === 'preview' ? 'active' : ''}`}
                                onClick={() => setMode('preview')}
                                title="Preview"
                                aria-label="Preview"
                            >
                                <Eye size={14} />
                                <span className="toggle-label">Preview</span>
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>

            {mode === 'edit' ? (
                <>
                    <CodeMirrorEditor
                        key={path}
                        content={effectiveContent ?? ''}
                        fontMode={fontClass === 'editor-mono' ? 'mono' : 'proportional'}
                        showLineNumbers={showLineNumbers}
                        isMobile={isMobile}
                        blameLines={(blameEnabled ? blame : diffBlame).lines}
                        blameEnabled={blameEnabled}
                        blameStale={blameEnabled ? blame.stale : false}
                        blameWidth={blameWidth}
                        onBlameWidthChange={handleBlameWidthChange}
                        onBlameWidthCommit={handleBlameWidthCommit}
                        readOnly={!canWrite}
                        onChange={handleUpdateContent}
                        onSelectionChange={handleSelectionChange}
                        editorViewRef={editorViewRef}
                        initialSelection={path ? cursorByPath.current.get(path) ?? 0 : 0}
                    />
                    {isMd && <MobileMarkdownToolbar onInsert={handleInsertMarkdown} disabled={!canWrite} />}
                </>
            ) : (
                <div className="editor-preview" onMouseUp={handlePreviewMouseUp}>
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkMath]}
                        rehypePlugins={[rehypeKatex, rehypeHighlight]}
                        skipHtml
                        components={markdownComponents}
                    >
                        {previewContent}
                    </ReactMarkdown>
                </div>
            )}
        </div>
    )
}
